import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";

const NAV = [
  { to: "/le-savoir-faire", label: "Savoir-faire" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/cabinets-d-expertise", label: "Cabinets d'expertise" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 backdrop-blur-md transition-all duration-500 ${
        scrolled
          ? "bg-background/95 border-b border-border"
          : "bg-background/70 border-b border-transparent"
      }`}
      data-testid="public-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-5 md:py-6 flex items-center justify-between gap-6">
        <Link to="/" className="flex flex-col leading-none group" data-testid="brand-link">
          <span className="font-serif text-2xl md:text-[27px] tracking-tight text-foreground transition-colors group-hover:text-primary">
            Luigi Chauveau
          </span>
          <span className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--gold))] mt-1.5">
            Menuisier · Agenceur
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9" data-testid="public-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-[12.5px] tracking-[0.04em] link-underline transition-colors ${
                  isActive ? "text-primary" : "text-foreground/85 hover:text-foreground"
                }`
              }
              data-testid={`nav-link-${n.to.replace(/\//g, "")}`}
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-primary transition-colors"
            data-testid="nav-cta-devis"
          >
            Demander un devis <ArrowUpRight size={12} />
          </Link>
        </nav>

        <button
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          className="md:hidden p-2 -mr-2 text-foreground"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <nav className="px-5 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `font-serif text-2xl ${isActive ? "text-primary" : "text-foreground"}`
                }
                data-testid={`mobile-nav-link-${n.to.replace(/\//g, "")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-[12px] tracking-[0.2em] uppercase font-medium self-start"
              data-testid="mobile-cta-devis"
            >
              Demander un devis <ArrowUpRight size={12} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-32 border-t border-border bg-background" data-testid="public-footer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="font-serif text-3xl md:text-4xl leading-tight max-w-md">
            Le bois travaillé avec patience, et tenu dans les délais.
          </p>
          <p className="text-serial mt-6">— Artisan depuis 2015</p>
        </div>
        <div className="md:col-span-2">
          <p className="text-eyebrow mb-4">Contact</p>
          <ul className="space-y-2 text-[14px]">
            <li>
              <a className="link-underline-static" href="tel:+33761051061" data-testid="footer-tel">
                07 61 05 10 61
              </a>
            </li>
            <li>
              <a
                className="link-underline-static"
                href="mailto:contact@luigichauveau.fr"
                data-testid="footer-email"
              >
                contact@luigichauveau.fr
              </a>
            </li>
            <li className="text-muted-foreground text-[13px] pt-2">SIRET 810 689 638 00015</li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-eyebrow mb-4">Le site</p>
          <ul className="space-y-2 text-[14px]">
            <li>
              <Link to="/le-savoir-faire" className="link-underline-static">
                Savoir-faire
              </Link>
            </li>
            <li>
              <Link to="/realisations" className="link-underline-static">
                Réalisations
              </Link>
            </li>
            <li>
              <Link to="/cabinets-d-expertise" className="link-underline-static">
                Cabinets d'expertise
              </Link>
            </li>
            <li>
              <Link to="/contact" className="link-underline-static">
                Demander un devis
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-eyebrow mb-4">Suivre</p>
          <ul className="space-y-2 text-[14px]">
            <li>
              <a
                href="https://instagram.com/luigichauveau"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline-static inline-flex items-center gap-2"
                data-testid="footer-instagram"
                aria-label="Instagram Luigi Chauveau"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                @luigichauveau
              </a>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-eyebrow mb-4">Loire-Atlantique</p>
          <ul className="space-y-2 text-[14px]">
            <li><Link to="/menuisier-nantes" className="link-underline-static">Nantes</Link></li>
            <li><Link to="/menuisier-bouguenais" className="link-underline-static">Bouguenais</Link></li>
            <li><Link to="/menuisier-la-baule" className="link-underline-static">La Baule</Link></li>
            <li><Link to="/menuisier-pornichet" className="link-underline-static">Pornichet</Link></li>
            <li><Link to="/menuisier-le-pouliguen" className="link-underline-static">Le Pouliguen</Link></li>
            <li><Link to="/menuisier-pornic" className="link-underline-static">Pornic</Link></li>
            <li><Link to="/menuisier-saint-brevin" className="link-underline-static">Saint-Brevin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-serial">
          <span>© {new Date().getFullYear()} Luigi Chauveau</span>
          <span className="md:order-3">Décennale et RC Pro à jour — attestations sur demande</span>
          <Link to="/mentions-legales" className="link-underline-static md:order-2">
            Mentions légales
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  const onAdmin = pathname.startsWith("/admin");
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      {/* Sticky mobile action bar — uniquement sur le public */}
      {!onAdmin && (
        <div
          className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-background border-t border-border shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)]"
          data-testid="mobile-sticky-bar"
        >
          <div className="grid grid-cols-2 divide-x divide-border">
            <a
              href="tel:+33761051061"
              className="flex items-center justify-center gap-2 py-3.5 text-[11.5px] tracking-[0.2em] uppercase font-medium text-foreground active:bg-muted"
              data-testid="mobile-bar-tel"
            >
              <Phone size={14} /> Appeler
            </a>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 py-3.5 bg-foreground text-background text-[11.5px] tracking-[0.2em] uppercase font-medium"
              data-testid="mobile-bar-devis"
            >
              Demander un devis <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
